import AutoComplete from "../../Common/Autocomplete/Autocomplete.tsx";
import { models } from "types/api/viewModels.ts";
import { matchSorter } from "match-sorter";
import { ColorMatchContainer } from "./addColorMatch.styles.ts";
import TextField from "@mui/material/TextField";
import { Skeleton } from "@mui/material";
import { Control, Controller, ControllerFieldState, UseFormClearErrors, UseFormSetValue } from "react-hook-form";
import { useEffect, useState } from "react";
import {
	CharacterLimitExceeded,
	ColorOptionText,
	CustomText,
	EnterColorCode,
	EnterVendorText,
	PaintVendorText
} from "constants/text.ts";
import HelperText from "../../Form/HelperText/HelperText.tsx";
import { CustomColorType } from "types/declarations/enums.ts";

const modListIdSymbol = Symbol("modId");

export type Modification = models["ModificationViewModel"] & {
	[modListIdSymbol]: string;
};

export const VENDOR_NOT_LISTED_DESCRIPTION = "The Vendor I’m Looking for is Not Listed";
export const COLOR_NOT_LISTED_DESCRIPTION = "Color Not Listed";
export const COLOR_CODE_EXAMPLE_HELPER_TEXT = "Please provide color code and name.";
export const CUSTOM_COLOR_COLOR_MATCH_TYPE = "Custom Color";
export const FINISH_SELECT_COLOR_MATCH_TYPE = "Finish Select";
export const COLOR_MATCH_COLOR_MATCH_TYPE = "Color Match";
export const UNKNOWN_COLOR_MATCH_TYPE = "Unknown";

const filterVendorsOptions = (items: models["CustomColorViewModel"][], { inputValue }: { inputValue: string }) => {
	const specialVendor = items.find((v) => v.description === VENDOR_NOT_LISTED_DESCRIPTION);
	let filtered = matchSorter(items, inputValue, { keys: ["description"] })
		.filter((v) => v.description !== VENDOR_NOT_LISTED_DESCRIPTION)
		.sort((a, b) => (a.description || "").localeCompare(b.description || ""));
	if (specialVendor) {
		filtered.push(specialVendor);
	}
	return filtered;
};

const filterColorsOptions = (items: models["CustomColorViewModel"][], { inputValue }: { inputValue: string }) => {
	const specialVendor = items.find((v) => v.description === COLOR_NOT_LISTED_DESCRIPTION);
	let filtered = matchSorter(items, inputValue, { keys: ["description", "code"] })
		.filter((v) => v.description !== COLOR_NOT_LISTED_DESCRIPTION)
		.sort((a, b) => (a.description || "").localeCompare(b.description || ""));
	if (specialVendor) {
		filtered.push(specialVendor);
	}
	return filtered;
};

const customColorTitle = (title: models["CustomColorType"] | undefined) => {
	switch (title) {
		case CustomColorType.ColorMatch:
			return COLOR_MATCH_COLOR_MATCH_TYPE;
		case CustomColorType.FinishSelect:
			return FINISH_SELECT_COLOR_MATCH_TYPE;
		case CustomColorType.CustomColor:
			return CUSTOM_COLOR_COLOR_MATCH_TYPE;
		default:
			return UNKNOWN_COLOR_MATCH_TYPE;
	}
};

const inputErrorHandling = (value: string | null | undefined, fieldState: ControllerFieldState | null | undefined) => {
	if (value && value?.length > 50) {
		return CharacterLimitExceeded;
	}

	if (fieldState?.error) {
		return fieldState.error.message;
	}
};

interface AddColorMatchProps {
	vendors: models["CustomColorVendorViewModel"][];
	colorMatchType: models["CustomColorType"] | undefined;
	isLoadingVendors?: boolean;
	control: Control<any>;
	setValue: UseFormSetValue<any>;
	defaultValues?: {
		paintVendor?: models["CustomColorVendorViewModel"] | null;
		colorOption?: models["CustomColorViewModel"] | null;
		colorCode?: string | null;
		customVendor?: string | null;
	};
	clearErrors: UseFormClearErrors<any>;
}

const AddColorMatch = ({
	vendors,
	isLoadingVendors,
	control,
	setValue,
	defaultValues,
	clearErrors,
	colorMatchType
}: AddColorMatchProps) => {
	const [vendor, setVendor] = useState<models["CustomColorVendorViewModel"] | null>(
		defaultValues?.paintVendor ?? null
	);
	const [color, setColor] = useState<models["CustomColorViewModel"] | null>(defaultValues?.colorOption ?? null);
	const [showColorOption, setShowColorOption] = useState(
		!!vendor && vendor.description !== VENDOR_NOT_LISTED_DESCRIPTION
	);
	const [showCustomVendor, setShowCustomVendor] = useState(vendor?.description === VENDOR_NOT_LISTED_DESCRIPTION);
	const [showColorCode, setShowColorCode] = useState(
		vendor?.description === VENDOR_NOT_LISTED_DESCRIPTION || color?.description === COLOR_NOT_LISTED_DESCRIPTION
	);
	let columns = [true, showCustomVendor, showColorOption, showColorCode].filter(Boolean).length;
	if (columns < 2) columns = 2;

	useEffect(() => {
		setVendor(defaultValues?.paintVendor ?? null);
		if (defaultValues?.paintVendor?.code === CustomText) {
			setShowCustomVendor(true);
			setShowColorCode(true);
			setShowColorOption(false);
			setValue("paintVendor", { code: CustomText, description: VENDOR_NOT_LISTED_DESCRIPTION });
			setValue("customVendor", defaultValues.paintVendor.description);
		}
		if (defaultValues?.colorOption?.code === CustomText) {
			setShowColorCode(true);
			setValue("colorOption", { code: CustomText, description: COLOR_NOT_LISTED_DESCRIPTION });
		}
	}, [defaultValues?.colorOption?.code, defaultValues?.paintVendor, setValue]);

	return (
		<div>
			<h6 style={{ marginBottom: "16px" }}>{customColorTitle(colorMatchType)}</h6>
			<ColorMatchContainer columns={columns}>
				{isLoadingVendors ? (
					<Skeleton
						height={40}
						variant="rectangular"
					/>
				) : (
					<Controller
						name="paintVendor"
						control={control}
						render={({ field: { onChange, value = defaultValues?.paintVendor ?? null }, fieldState }) => (
							<AutoComplete
								options={vendors ?? []}
								onChange={(_e, value) => {
									onChange(value);
									setVendor(value);
									setColor(null);
									setValue("colorOption", null);
									setValue("colorCode", null);
									setValue("customVendor", null);
									clearErrors("paintVendor");
									if (value?.code === CustomText) {
										setShowColorCode(true);
										setShowCustomVendor(true);
										setShowColorOption(false);
									} else {
										setShowColorCode(false);
										setShowCustomVendor(false);
										setShowColorOption(!!value);
									}
								}}
								defaultValue={defaultValues?.paintVendor}
								getOptionLabel={(option: models["CustomColorVendorViewModel"]) =>
									option.code === CustomText
										? VENDOR_NOT_LISTED_DESCRIPTION
										: (option.description ?? "")
								}
								isError={!!fieldState.error}
								errorText={fieldState.error?.message || ""}
								label={PaintVendorText}
								dataTestId="vendor-selection"
								filterOptions={filterVendorsOptions}
								required
								value={value}
							/>
						)}
					/>
				)}

				{showCustomVendor && (
					<Controller
						name="customVendor"
						control={control}
						render={({ field: { onChange, value = defaultValues?.customVendor ?? null }, fieldState }) => (
							<TextField
								label={EnterVendorText}
								size="small"
								data-testid="custom-vendor-field"
								required
								error={!!fieldState.error || value?.length > 50}
								helperText={
									<HelperText
										isError={value?.length > 50 || !!fieldState.error}
										text={inputErrorHandling(value, fieldState)}
										characterLimit={{
											currentLength: value?.length || 0,
											limit: 50,
											isLimitError: value?.length > 50
										}}
									/>
								}
								value={value || ""}
								onChange={(e) => {
									setValue("customVendor", e.target.value);
									clearErrors("customVendor");
									onChange(e.target.value);
								}}
							/>
						)}
					/>
				)}
				{showColorOption && (
					<Controller
						name="colorOption"
						control={control}
						render={({ field: { onChange, value = defaultValues?.colorOption ?? null }, fieldState }) => (
							<AutoComplete
								options={vendor?.colors ?? []}
								onChange={(_e, value) => {
									onChange(value);
									setColor(value);
									setValue("colorCode", null);
									clearErrors("colorOption");
									if (value?.code === CustomText) {
										setShowColorCode(true);
									} else {
										setShowColorCode(false);
									}
								}}
								isError={!!fieldState.error}
								errorText={fieldState.error?.message || ""}
								getOptionLabel={(option: models["CustomColorViewModel"]) =>
									[option.code === CustomText ? undefined : option.code, option.description]
										.filter(Boolean)
										.join(" - ") ?? ""
								}
								defaultValue={defaultValues?.colorOption}
								filterOptions={filterColorsOptions}
								label={ColorOptionText}
								dataTestId="color-option-selection"
								required
								value={value}
							/>
						)}
					/>
				)}

				{showColorCode && (
					<Controller
						name="colorCode"
						control={control}
						render={({ field: { onChange, value = defaultValues?.colorCode ?? null }, fieldState }) => (
							<TextField
								label={EnterColorCode}
								size="small"
								data-testid="color-code-field"
								required
								error={!!fieldState.error || value?.length > 50}
								helperText={
									<HelperText
										isError={value?.length > 50 || !!fieldState.error}
										text={inputErrorHandling(value, fieldState) || COLOR_CODE_EXAMPLE_HELPER_TEXT}
										characterLimit={{
											currentLength: value?.length || 0,
											limit: 50,
											isLimitError: value?.length > 50
										}}
									/>
								}
								value={value || ""}
								onChange={(e) => {
									clearErrors("colorCode");
									onChange(e.target.value);
								}}
							/>
						)}
					/>
				)}
			</ColorMatchContainer>
		</div>
	);
};

export default AddColorMatch;
